import { Grid, IconButton, Stack, Tooltip, Typography } from '@mui/material';

import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { upperFirst } from 'lodash';

import { ReactComponent as DrawerClose } from 'common/assets/images/drawer-close.svg';
import { TabPanel } from 'common/components/TabBar/TabBar';
import {
	StyledLinedTabs,
	StyledLinedTab,
} from 'common/components/TabBar/TabBarList';
import { RootState } from 'common/store/AppStore';
import { setMyApprovalData } from 'common/store/reducer/commonReducer';

import useTableQuery from 'hooks/useTableQuery';

import MyApprovalChangesTab from '../MyApprovalChanges/MyApprovalsChangesTab';
import MyApprovalOverviewTab from '../MyApprovalOverview/MyApprovalOverviewTab';
import { TAB_NAMES } from '../MyApprovalUtils';

import styles from './ApprovalDrawer.module.scss';

interface IApprovalDrawerProps {
	data: any;
	onClose: () => void;
	defaultTab?: number;
}

const { OVERVIEW_TAB, CHANGES_TAB } = TAB_NAMES;

const ApprovalDrawer = (props: IApprovalDrawerProps) => {
	const dispatch = useDispatch();
	const { data, onClose, defaultTab = 0 } = props;

	const { drawerCloseIconColor } = styles;

	const entityId = data?.id;
	const entityType = upperFirst(data?.type);
	const changesCount = data?.changeCount;

	const approvalDrawerTitle = useSelector(
		(state: RootState) => state.common.approvalDrawerTitle,
	);
	const [selectedIndex, setSelectedIndex] = useState<number>(0);

	const { tableQueryObj, updateTableQuery } = useTableQuery({
		id: 'relationMappingTable',
		defaultPageSize: 10,
	});

	useEffect(() => {
		setSelectedIndex(defaultTab);
	}, [defaultTab]);

	const handleTabChange = (event: any, newIndex: number) => {
		setSelectedIndex(newIndex);
	};

	const handleDrawerChange = () => {
		dispatch(setMyApprovalData([]));
		updateTableQuery({ ...tableQueryObj, pageNumber: 0 });
		onClose();
	};

	const tabs = [
		{
			id: 1,
			label: OVERVIEW_TAB,
			count: 0,
			children: (
				<MyApprovalOverviewTab entityId={entityId} entityType={entityType} />
			),
		},
		{
			id: 2,
			label: CHANGES_TAB,
			count: changesCount,
			children: (
				<MyApprovalChangesTab
					entityId={entityId}
					entityType={entityType}
					entityName={approvalDrawerTitle}
				/>
			),
		},
	];

	return (
		<div>
			<Stack
				className={styles['drawer-top']}
				direction="row"
				data-testid="side-drawer"
				justifyContent="space-between"
			>
				<Tooltip title={approvalDrawerTitle} placement="bottom">
					<Typography variant="h1" className={styles['fileupload-text-name']}>
						{approvalDrawerTitle}
					</Typography>
				</Tooltip>

				<IconButton
					size="large"
					onClick={handleDrawerChange}
					data-testid="close-button-icon"
					className={styles['fileupload-close']}
					disableRipple
				>
					<DrawerClose fill={drawerCloseIconColor} />
				</IconButton>
			</Stack>
			<Grid container>
				<Grid item xs={12} className={styles['tabs-item']}>
					<div className={styles['tabs-width']}>
						<StyledLinedTabs
							value={selectedIndex}
							onChange={handleTabChange}
							aria-label="Approval Drawer Tabs"
						>
							{tabs.map((tab) => (
								<StyledLinedTab
									key={tab.id}
									label={`${tab.label} ${
										tab.count > 0 ? `(${tab.count})` : ''
									}`}
									sx={{
										alignItems: 'flex-start',
									}}
								/>
							))}
						</StyledLinedTabs>
					</div>
				</Grid>

				<Grid item xs={12}>
					{tabs.map((tab, index) => (
						<TabPanel
							value={selectedIndex}
							index={index}
							key={`tab-panel-${tab.label}`}
						>
							{tab.children}
						</TabPanel>
					))}
				</Grid>
			</Grid>
		</div>
	);
};

export default ApprovalDrawer;
